import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { authFetch, isAuthenticated } from '../utils/auth';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle, User, Phone, Mail, MapPin, Dog, Calendar, AlertTriangle, Loader2, Heart, ChevronDown, ChevronUp, Truck, Clock, DollarSign, RotateCcw } from 'lucide-react';
import { getErrorMessage } from '../utils/errors';

// Mobile vet clinics that deliver pets directly to Pure Souls
const MOBILE_VET_CLINICS = ['Sunset Pets', 'NQ Vets Mobile', 'Townsville Mobile Vet', 'Fluffy Pawtential Vet'];

// NQ Care specific — collects payment on behalf of Pure Souls
const NQ_CARE_CLINIC = 'NQ Care';

// Pricing table by pet type and weight
const getServicePrice = (petType, weightKg) => {
  const weight = parseFloat(weightKg) || 0;
  const type = (petType || '').toLowerCase();
  if (type === 'cat') return 450;
  if (type === 'exotic pet') {
    if (weight <= 1.9) return 250;
    // Exotic over 1.9kg uses dog pricing
    if (weight <= 10) return 450;
    if (weight <= 20) return 500;
    if (weight <= 30) return 550;
    if (weight <= 40) return 600;
    if (weight <= 50) return 650;
    if (weight <= 60) return 700;
    if (weight <= 70) return 800;
    return 890;
  }
  if (type === 'dog') {
    if (weight <= 10) return 450;
    if (weight <= 20) return 500;
    if (weight <= 30) return 550;
    if (weight <= 40) return 600;
    if (weight <= 50) return 650;
    if (weight <= 60) return 700;
    if (weight <= 70) return 800;
    return 890;
  }
  return 0;
};

const NewRequest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [petRecordId, setPetRecordId] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [staffBenefitOpen, setStaffBenefitOpen] = useState(false);

  // Detect if this is a mobile vet clinic
  const clinic = JSON.parse(localStorage.getItem('vet_portal_clinic') || '{}');
  const isMobileVet = MOBILE_VET_CLINICS.includes(clinic.clinic_name);
  const isNQCare = clinic.clinic_name === NQ_CARE_CLINIC;
  // Sunset Pets operate from home, so their pickups are vet collections, not home collections
  const isEmailOptional = clinic.clinic_name === 'Sunset Pets';
  const hasVetCollectOption = clinic.clinic_name === 'Sunset Pets';

  // NQ Care payment tracking — null = not answered yet (forces explicit choice)
  const [clientPaid, setClientPaid] = useState(null);
  const [amountPaid, setAmountPaid] = useState('');

  // Pre-booked duplicate detection
  const [preBookedPets, setPreBookedPets] = useState([]);
  const [matchedPreBooked, setMatchedPreBooked] = useState(null);
  const [dismissedMatch, setDismissedMatch] = useState(false);
  const [showActivateForm, setShowActivateForm] = useState(false);
  const [activateMicrochip, setActivateMicrochip] = useState('');
  const [activateEmotional, setActivateEmotional] = useState('');
  const [activateNotes, setActivateNotes] = useState('');
  const [activating, setActivating] = useState(false);
  
  // Get today's date in Townsville timezone (Australia/Brisbane)
  const getTownsvilleDate = () => {
    const now = new Date();
    const townsvilleTime = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
    const year = townsvilleTime.getFullYear();
    const month = String(townsvilleTime.getMonth() + 1).padStart(2, '0');
    const day = String(townsvilleTime.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [formData, setFormData] = useState({
    estimated_pickup_date: getTownsvilleDate(),
    pet_name: '',
    pet_type: '',
    breed: '',
    weight_kg: '',
    sex: '',
    age_years: '',
    age_months: '',
    microchip_number: '',
    client_name: '',
    phone_number: '',
    email: '',
    address: '',
    postcode: '',
    collection_notes: '',
    urgent: false,
    vet_staff_benefit: 'no_benefit',
    owner_emotional_state: '',
    planned_delivery_datetime: '',
    planned_collection_datetime: isMobileVet && !hasVetCollectOption ? 'active' : '',
    client_dropoff_datetime: '',
    // 'vet' = Pure Souls collects from the vet premises (day only, no time)
    transport_mode: hasVetCollectOption ? 'vet' : 'collection',
    collection_time: '09:00',
    delivery_time: '09:00',
    client_time: '09:00',
    euthanasia_datetime: '',
    euthanasia_date: '',
    euthanasia_time: ''
  });

  // Address autocomplete state
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressDebounceRef = useRef(null);
  const addressWrapperRef = useRef(null);

  // ---- Draft recovery (device-only, per clinic) ----
  const draftKey = `vet_portal_draft_${clinic.email || clinic.clinic_name || 'unknown'}`;
  const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  const [foundDraft, setFoundDraft] = useState(null);
  const draftDebounceRef = useRef(null);

  const hasContent = (data) => !!((data?.pet_name || '').trim() || (data?.client_name || '').trim());

  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      const age = Date.now() - (draft.savedAt || 0);
      if (age > DRAFT_MAX_AGE_MS || !hasContent(draft.formData)) {
        localStorage.removeItem(draftKey);
        return;
      }
      setFoundDraft(draft);
    } catch {
      localStorage.removeItem(draftKey);
    }
  }, [draftKey]);

  // Silent autosave — paused while the restore banner is awaiting a decision
  useEffect(() => {
    if (foundDraft || success) return;
    if (!hasContent(formData)) return;
    clearTimeout(draftDebounceRef.current);
    draftDebounceRef.current = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify({
          savedAt: Date.now(),
          formData,
          clientPaid,
          amountPaid,
        }));
      } catch {
        // storage full or unavailable — nothing we can do, keep the form working
      }
    }, 800);
    return () => clearTimeout(draftDebounceRef.current);
  }, [formData, clientPaid, amountPaid, foundDraft, success, draftKey]);

  const restoreDraft = () => {
    setFormData(prev => ({ ...prev, ...foundDraft.formData }));
    if (foundDraft.clientPaid !== undefined) setClientPaid(foundDraft.clientPaid);
    if (foundDraft.amountPaid !== undefined) setAmountPaid(foundDraft.amountPaid);
    setFoundDraft(null);
    toast.success('Unfinished request restored');
  };

  const discardDraft = () => {
    localStorage.removeItem(draftKey);
    setFoundDraft(null);
  };

  const draftAgeLabel = (savedAt) => {
    const mins = Math.round((Date.now() - savedAt) / 60000);
    if (mins < 1) return 'saved moments ago';
    if (mins < 60) return `saved ${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `saved ${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    const days = Math.round(hrs / 24);
    return `saved ${days} day${days === 1 ? '' : 's'} ago`;
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    // Silently fetch pre-booked pets in background for duplicate detection
    authFetch('/api/pets/status').then(r => r.ok ? r.json() : null).then(data => {
      if (data?.pets) {
        setPreBookedPets(data.pets.filter(p => p.workflow_stage === 'prepayment'));
      }
    }).catch(() => {});
  }, [navigate]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (addressWrapperRef.current && !addressWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);

    // Also keep activate fields in sync if vet keeps typing after match found
    if (field === 'microchip_number' && matchedPreBooked) setActivateMicrochip(value);
    if (field === 'owner_emotional_state' && matchedPreBooked) setActivateEmotional(value);
    if (field === 'collection_notes' && matchedPreBooked) setActivateNotes(value);
    // Auto-update NQ Care price when pet type or weight changes
    if (isNQCare && (field === 'pet_type' || field === 'weight_kg')) {
      const petType = field === 'pet_type' ? value : updated.pet_type;
      const weight = field === 'weight_kg' ? value : updated.weight_kg;
      if (petType && weight) {
        setAmountPaid(String(getServicePrice(petType, weight)));
      }
    }
    if ((field === 'pet_name' || field === 'client_name') && !dismissedMatch && preBookedPets.length > 0) {
      const petVal = (field === 'pet_name' ? value : updated.pet_name).toLowerCase().trim();
      const clientVal = (field === 'client_name' ? value : updated.client_name).toLowerCase().trim();
      if (petVal.length >= 3 || clientVal.length >= 3) {
        const match = preBookedPets.find(p => {
          const pName = (p.pet_name || '').toLowerCase();
          const cName = (p.client_name || '').toLowerCase();
          return (petVal.length >= 3 && pName.includes(petVal)) ||
                 (clientVal.length >= 3 && cName.includes(clientVal));
        });
        setMatchedPreBooked(match || null);
        if (match) {
          setShowActivateForm(false);
          // Pre-copy already-entered values when a match is found
          setActivateMicrochip(updated.microchip_number || '');
          setActivateEmotional(updated.owner_emotional_state || '');
          setActivateNotes(updated.collection_notes || '');
        }
      } else {
        setMatchedPreBooked(null);
      }
    }
  };

  // Debounced address search using Google Places API via backend proxy
  const handleActivatePreBooked = async () => {
    if (!matchedPreBooked) return;
    const petId = matchedPreBooked.pet_id || matchedPreBooked.pet_record_id;
    setActivating(true);
    try {
      const payload = {};
      if (activateMicrochip) payload.microchip_number = activateMicrochip;
      if (activateEmotional) payload.owner_emotional_state = activateEmotional;
      if (activateNotes) payload.collection_notes = activateNotes;
      const res = await authFetch(`/api/pets/activate-prepayment/${petId}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${matchedPreBooked.pet_name} activated for collection!`, {
          description: 'No new submission needed — the pre-booking has been activated.'
        });
        localStorage.removeItem(draftKey);
        navigate('/pet-tracking');
      } else {
        toast.error(getErrorMessage(data.detail, 'Activation failed'));
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setActivating(false);
    }
  };

  const searchAddress = useCallback((query) => {
    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setAddressLoading(true);
    addressDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/address-autocomplete`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ input: query }),
          }
        );
        const data = await res.json();
        const suggestions = (data.suggestions || [])
          .filter(s => s.placePrediction)
          .map(s => ({
            placeId: s.placePrediction.placeId,
            main: s.placePrediction.structuredFormat?.mainText?.text || '',
            secondary: s.placePrediction.structuredFormat?.secondaryText?.text || '',
            full: s.placePrediction.text?.text || '',
          }));
        setAddressSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      } catch {
        setAddressSuggestions([]);
      } finally {
        setAddressLoading(false);
      }
    }, 300);
  }, []);

  const selectAddress = async (suggestion) => {
    setShowSuggestions(false);
    handleChange('address', suggestion.full);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/address-details/${suggestion.placeId}`
      );
      const data = await res.json();
      const components = data.addressComponents || [];
      const postcodeComp = components.find(c => c.types?.includes('postal_code'));
      const streetNum = components.find(c => c.types?.includes('street_number'));
      const route = components.find(c => c.types?.includes('route'));
      const locality = components.find(c => c.types?.includes('locality'));
      const state = components.find(c => c.types?.includes('administrative_area_level_1'));
      const parts = [
        [streetNum?.longText, route?.longText].filter(Boolean).join(' '),
        locality?.longText,
        state?.shortText,
      ].filter(Boolean).join(', ');
      // Update address and postcode in a single state update to avoid stale closure
      setFormData(prev => ({
        ...prev,
        address: parts || suggestion.full,
        postcode: postcodeComp ? (postcodeComp.longText || postcodeComp.shortText) : prev.postcode
      }));
    } catch (err) {
      console.error('Address details fetch failed:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate weight
    if (!formData.weight_kg) {
      toast.error('Please select the pet weight before submitting');
      return;
    }
    // Validate age
    if (!formData.age_years && formData.age_years !== '0') {
      toast.error('Please enter the pet age before submitting');
      return;
    }
    // NQ Care: must confirm payment question
    if (isNQCare && clientPaid === null) {
      toast.error('Please confirm whether the client paid at NQ Care');
      return;
    }

    // Client email: optional for Sunset Pets only (they don't record it), but if
    // one IS entered it must be valid — the CRM rejects malformed addresses
    const trimmedEmail = (formData.email || '').trim();
    if (!isEmailOptional || trimmedEmail) {
      if (!/^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(trimmedEmail)) {
        toast.error('Please enter a valid client email', {
          description: 'It needs a full domain, for example name@example.com'
        });
        return;
      }
    }
    // Collection day cannot be in the past (Brisbane time). Sundays are allowed.
    if (formData.estimated_pickup_date < getTownsvilleDate()) {
      toast.error('Collection date cannot be in the past');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        email: (formData.email || '').trim(),
        weight_kg: parseFloat(formData.weight_kg),
        age_years: formData.age_years ? parseInt(formData.age_years) : null,
        age_months: formData.age_months ? parseInt(formData.age_months) : null,
        vet_staff_benefit: formData.vet_staff_benefit === 'no_benefit' ? null : formData.vet_staff_benefit,
        owner_emotional_state: formData.owner_emotional_state || null,
        planned_delivery_datetime: formData.planned_delivery_datetime
          ? `${formData.estimated_pickup_date}T${formData.delivery_time}:00` : null,
        planned_collection_datetime: formData.planned_collection_datetime
          ? `${formData.estimated_pickup_date}T${formData.collection_time}:00` : null,
        client_dropoff_datetime: formData.client_dropoff_datetime
          ? `${formData.estimated_pickup_date}T${formData.client_time}:00` : null,
        euthanasia_datetime: formData.euthanasia_date
          ? `${formData.euthanasia_date}T${formData.euthanasia_time || '09:00'}:00`
          : null,
        client_paid: isNQCare ? (clientPaid === true) : false,
        amount_paid: isNQCare && clientPaid === true && amountPaid ? parseFloat(amountPaid) : null,
        // Tells the CRM which appointment type to book. Only sent for mobile vets,
        // who are the only clinics that choose a transport scenario.
        collection_type: isMobileVet ? {
          vet: 'vet_collection',
          collection: 'home_collection',
          delivery: 'vet_delivery',
          client: 'client_dropoff',
        }[formData.transport_mode] || null : null,
      };

      const response = await authFetch('/api/collection-requests', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Collection request submitted', {
          description: 'Our pet loving team will organise collection promptly.'
        });
        setPetRecordId(data.pet_record_id);
        setSuccess(true);
        localStorage.removeItem(draftKey);
      } else {
        toast.error(getErrorMessage(data.detail, 'Failed to submit request'));
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Layout>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto py-12"
        >
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-12 pb-12 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
                className="inline-flex p-4 rounded-full bg-[hsl(var(--success-light))] mb-6"
              >
                <CheckCircle2 className="h-16 w-16 text-[hsl(var(--success))]" />
              </motion.div>
              <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Request Submitted Successfully!
              </h2>
              <p className="text-muted-foreground mb-6">
                Your pet collection request has been submitted to Pure Souls.
              </p>
              {petRecordId && (
                <div className="bg-primary/5 rounded-xl p-5 mb-6 border border-primary/10">
                  <p className="text-lg font-medium text-primary mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Entrusted to Pure Souls
                  </p>
                  <p className="text-sm text-muted-foreground">
                    We'll take loving care of this precious companion. Track the journey in real time from your Pet Tracking page.
                  </p>
                </div>
              )}
              <div className="flex gap-3 justify-center">
                <Button onClick={() => navigate('/pet-tracking')} variant="default">
                  Track Pet Status
                </Button>
                <Button onClick={() => navigate('/dashboard')} variant="outline">
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="new-request-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold mb-2 text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            New Collection Request
          </h1>
          <p className="text-muted-foreground text-lg">
            Submit a pet collection request for water cremation services
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Unfinished draft recovery */}
          {foundDraft && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[hsl(var(--info-light))] border-l-4 border-[hsl(var(--info))] rounded-lg p-4"
              data-testid="draft-recovery-banner"
            >
              <div className="flex items-start gap-3">
                <RotateCcw className="h-5 w-5 text-[hsl(var(--info))] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[hsl(var(--info))]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Unfinished request found
                  </p>
                  <p className="text-sm text-muted-foreground break-words">
                    {[foundDraft.formData?.pet_name && `Pet: ${foundDraft.formData.pet_name}`,
                      foundDraft.formData?.client_name && `Client: ${foundDraft.formData.client_name}`]
                      .filter(Boolean).join(' · ')}
                    {foundDraft.savedAt ? ` — ${draftAgeLabel(foundDraft.savedAt)}` : ''}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button type="button" size="sm" onClick={restoreDraft} data-testid="draft-restore-btn">
                      Restore details
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={discardDraft} data-testid="draft-discard-btn">
                      Start fresh
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Emergency Toggle Banner */}
          {formData.urgent && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-[hsl(var(--warning-light))] border-l-4 border-[hsl(var(--warning))] rounded-lg p-4 flex items-center gap-3"
            >
              <AlertTriangle className="h-5 w-5 text-[hsl(var(--warning))]" />
              <div>
                <p className="font-semibold text-[hsl(var(--warning))]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Emergency Collection Request
                </p>
                <p className="text-sm text-muted-foreground">This request will be prioritized for immediate processing</p>
              </div>
            </motion.div>
          )}

          {/* Date Field - First Field */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
          >
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[hsl(var(--info-light))]">
                    <Calendar className="h-5 w-5 text-[hsl(var(--info))]" />
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      {isMobileVet ? 'Select Booking Date' : 'Collection Date'}
                    </CardTitle>
                    <CardDescription>{isMobileVet ? '' : 'When should we collect the pet?'}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="estimated_pickup_date">Date *</Label>
                    <Input
                      id="estimated_pickup_date"
                      type="date"
                      value={formData.estimated_pickup_date}
                      onChange={(e) => handleChange('estimated_pickup_date', e.target.value)}
                      min={getTownsvilleDate()}
                      required
                      data-testid="pickup-date-input"
                      className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                    />
                  </div>

                  {/* Mobile vet clinics: transport options */}
                  {isMobileVet && (() => {
                    const activeTransport = formData.transport_mode;
                    return (
                    <div className="border-t border-border/50 pt-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <Truck className="h-4 w-4 text-primary flex-shrink-0" />
                        <p className="text-sm font-semibold">Pet transport logistics</p>
                      </div>
                      <div className={`grid gap-2 ${hasVetCollectOption ? 'grid-cols-2' : 'grid-cols-3'}`}>
                        {hasVetCollectOption && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, planned_delivery_datetime: '', client_dropoff_datetime: '', planned_collection_datetime: '', transport_mode: 'vet' }));
                            }}
                            className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                              activeTransport === 'vet'
                                ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                                : 'border-border hover:border-primary/30'
                            }`}
                            data-testid="transport-vet"
                          >
                            <p className="text-sm font-semibold">Pure Souls Collects from Vet</p>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, planned_delivery_datetime: '', client_dropoff_datetime: '', planned_collection_datetime: 'active', transport_mode: 'collection' }));
                          }}
                          className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                            activeTransport === 'collection'
                              ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                              : 'border-border hover:border-primary/30'
                          }`}
                          data-testid="transport-collection"
                        >
                          <p className="text-sm font-semibold">Pure Souls Collects from home</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, planned_collection_datetime: '', client_dropoff_datetime: '', planned_delivery_datetime: 'active', transport_mode: 'delivery' }));
                          }}
                          className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                            activeTransport === 'delivery'
                              ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                              : 'border-border hover:border-primary/30'
                          }`}
                          data-testid="transport-delivery"
                        >
                          <p className="text-sm font-semibold">Vet Mobile transfers to Pure Souls</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, planned_collection_datetime: '', planned_delivery_datetime: '', client_dropoff_datetime: 'active', transport_mode: 'client' }));
                          }}
                          className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                            activeTransport === 'client'
                              ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                              : 'border-border hover:border-primary/30'
                          }`}
                          data-testid="transport-client"
                        >
                          <p className="text-sm font-semibold">Client transports pet to Pure Souls</p>
                        </button>
                      </div>

                      {/* Vet collection: day only, the CRM sets the time */}
                      {activeTransport === 'vet' && (
                        <div className="space-y-1 bg-primary/5 rounded-xl p-3 border border-primary/10" data-testid="vet-collection-info">
                          <p className="text-sm font-semibold">Vet Collection</p>
                          <p className="text-xs text-muted-foreground">
                            A "Vet Collection" appointment will be created in our calendar for the
                            selected day, the same as every other clinic. No collection time is needed
                            &mdash; our team schedules it on their run.
                          </p>
                        </div>
                      )}

                      {/* Collection time picker */}
                      {activeTransport === 'collection' && (
                        <div className="space-y-2 bg-primary/5 rounded-xl p-3 border border-primary/10">
                          <Label htmlFor="collection_time">Estimated Collection Time</Label>
                          <Input
                            id="collection_time"
                            type="time"
                            value={formData.collection_time}
                            onChange={(e) => handleChange('collection_time', e.target.value)}
                            data-testid="collection-time-input"
                            className="transition-all duration-200 focus:ring-2 focus:ring-primary/20 bg-white"
                          />
                          <p className="text-xs text-muted-foreground">
                            A "Pet Collection" appointment will be created in our calendar
                          </p>
                        </div>
                      )}

                      {/* Delivery time picker */}
                      {activeTransport === 'delivery' && (
                        <div className="space-y-2 bg-primary/5 rounded-xl p-3 border border-primary/10">
                          <Label htmlFor="delivery_time">Estimated Delivery Time</Label>
                          <Input
                            id="delivery_time"
                            type="time"
                            value={formData.delivery_time}
                            onChange={(e) => handleChange('delivery_time', e.target.value)}
                            data-testid="delivery-time-input"
                            className="transition-all duration-200 focus:ring-2 focus:ring-primary/20 bg-white"
                          />
                          <p className="text-xs text-muted-foreground">
                            A "Pet Drop off" appointment will be created in our calendar
                          </p>
                        </div>
                      )}

                      {/* Client drop-off time picker */}
                      {activeTransport === 'client' && (
                        <div className="space-y-2 bg-primary/5 rounded-xl p-3 border border-primary/10">
                          <Label htmlFor="client_time">Estimated Client Drop-off Time</Label>
                          <Input
                            id="client_time"
                            type="time"
                            value={formData.client_time}
                            onChange={(e) => handleChange('client_time', e.target.value)}
                            data-testid="client-time-input"
                            className="transition-all duration-200 focus:ring-2 focus:ring-primary/20 bg-white"
                          />
                          <p className="text-xs text-muted-foreground">
                            A "Client Drop off" appointment will be created in our calendar
                          </p>
                        </div>
                      )}
                    </div>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Euthanasia Date/Time — mobile vets only */}
          {isMobileVet && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.07 }}
            >
              <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[hsl(var(--info-light))]">
                      <Clock className="h-5 w-5 text-[hsl(var(--info))]" />
                    </div>
                    <div>
                      <CardTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Euthanasia Date & Time</CardTitle>
                      <CardDescription>When was or will the euthanasia be performed?</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="euthanasia_date">Date</Label>
                      <Input
                        id="euthanasia_date"
                        type="date"
                        value={formData.euthanasia_date}
                        onChange={(e) => handleChange('euthanasia_date', e.target.value)}
                        data-testid="euthanasia-date-input"
                        className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="euthanasia_time">Time</Label>
                      <Input
                        id="euthanasia_time"
                        type="time"
                        value={formData.euthanasia_time}
                        onChange={(e) => handleChange('euthanasia_time', e.target.value)}
                        data-testid="euthanasia-time-input"
                        className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Helps our team plan timing accordingly</p>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Client Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-accent/10">
                    <User className="h-5 w-5 text-[hsl(var(--accent))]" />
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Client Information</CardTitle>
                    <CardDescription>Details of the pet owner</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="client_name">Client Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="client_name"
                      value={formData.client_name}
                      onChange={(e) => handleChange('client_name', e.target.value)}
                      required
                      data-testid="client-name-input"
                      className="pl-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone_number">Phone Number *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="phone_number"
                        type="tel"
                        value={formData.phone_number}
                        onChange={(e) => handleChange('phone_number', e.target.value)}
                        required
                        data-testid="phone-input"
                        className="pl-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email {isEmailOptional ? '(optional)' : '*'}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required={!isEmailOptional}
                        placeholder={isEmailOptional ? 'Leave blank if not recorded' : undefined}
                        data-testid="email-input"
                        className="pl-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2" ref={addressWrapperRef}>
                  <Label htmlFor="address">Address *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => {
                        handleChange('address', e.target.value);
                        searchAddress(e.target.value);
                      }}
                      onFocus={() => { if (addressSuggestions.length > 0) setShowSuggestions(true); }}
                      placeholder="Start typing an address..."
                      required
                      autoComplete="off"
                      data-testid="address-input"
                      className="pl-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                    />
                    {addressLoading && (
                      <Loader2 className="absolute right-3 top-3 h-4 w-4 text-muted-foreground animate-spin" />
                    )}
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                        {addressSuggestions.map((s, i) => (
                          <button
                            key={s.placeId || i}
                            type="button"
                            onClick={() => selectAddress(s)}
                            className="w-full text-left px-4 py-3 hover:bg-primary/5 transition-colors border-b border-border/50 last:border-0 cursor-pointer"
                            data-testid={`address-suggestion-${i}`}
                          >
                            <p className="text-sm font-medium truncate">{s.main}</p>
                            <p className="text-xs text-muted-foreground truncate">{s.secondary}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postcode">Postcode *</Label>
                  <Input
                    id="postcode"
                    value={formData.postcode}
                    onChange={(e) => handleChange('postcode', e.target.value)}
                    required
                    readOnly={!!formData.postcode}
                    data-testid="postcode-input"
                    className={`transition-all duration-200 focus:ring-2 focus:ring-primary/20 ${formData.postcode ? 'bg-muted/50 text-muted-foreground cursor-default' : ''}`}
                  />
                  {formData.postcode && (
                    <p className="text-xs text-muted-foreground">Auto-filled from address. <button type="button" className="text-primary underline cursor-pointer" onClick={() => handleChange('postcode', '')}>Edit</button></p>
                  )}
                </div>

                {/* Owner Emotional State */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-semibold">Owner Emotional State</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">This helps our team communicate with the family in the most supportive way.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="emotional-state-selector">
                    {[
                      { value: 'very_distressed', label: 'Very Distressed', desc: 'Highly emotional, needs extra compassion', dot: 'bg-red-500', ring: 'border-red-300 bg-red-50', inactive: 'border-border hover:border-red-200 hover:bg-red-50/50' },
                      { value: 'emotional',        label: 'Emotional',        desc: 'Upset but able to communicate normally',  dot: 'bg-orange-400', ring: 'border-orange-300 bg-orange-50', inactive: 'border-border hover:border-orange-200 hover:bg-orange-50/50' },
                      { value: 'calm',             label: 'Calm',             desc: 'Emotionally stable or prepared',          dot: 'bg-green-500', ring: 'border-green-300 bg-green-50', inactive: 'border-border hover:border-green-200 hover:bg-green-50/50' },
                      { value: 'not_present',      label: 'Not Present',      desc: 'Owner not present at drop-off',           dot: 'bg-gray-400', ring: 'border-gray-300 bg-gray-50', inactive: 'border-border hover:border-gray-200 hover:bg-gray-50/50' },
                      { value: 'unsure',           label: 'Unsure',           desc: 'Staff unsure of emotional state',         dot: 'bg-slate-400', ring: 'border-slate-300 bg-slate-50', inactive: 'border-border hover:border-slate-200 hover:bg-slate-50/50' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleChange('owner_emotional_state', formData.owner_emotional_state === opt.value ? '' : opt.value)}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                          formData.owner_emotional_state === opt.value ? opt.ring + ' shadow-sm' : opt.inactive
                        }`}
                        data-testid={`emotional-state-${opt.value}`}
                      >
                        <span className={`w-3 h-3 rounded-full flex-shrink-0 ${opt.dot}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold leading-tight">{opt.label}</p>
                          <p className="text-[10px] text-muted-foreground leading-tight mt-0.5 truncate">{opt.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Pet Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Dog className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Pet Information</CardTitle>
                    <CardDescription>Details about the pet for collection</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="pet_name">Pet Name *</Label>
                    <Input
                      id="pet_name"
                      value={formData.pet_name}
                      onChange={(e) => handleChange('pet_name', e.target.value)}
                      required
                      data-testid="pet-name-input"
                      className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pet_type">Pet Type *</Label>
                    <Select value={formData.pet_type} onValueChange={(value) => handleChange('pet_type', value)} required>
                      <SelectTrigger data-testid="pet-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dog">Dog</SelectItem>
                        <SelectItem value="cat">Cat</SelectItem>
                        <SelectItem value="exotic pet">Exotic Pet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="breed">Breed *</Label>
                    <Input
                      id="breed"
                      value={formData.breed}
                      onChange={(e) => handleChange('breed', e.target.value)}
                      required
                      data-testid="breed-input"
                      className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="weight_kg">Weight (kg) *</Label>
                    <Select 
                      value={formData.weight_kg} 
                      onValueChange={(value) => handleChange('weight_kg', value)}
                      required
                    >
                      <SelectTrigger data-testid="weight-select">
                        <SelectValue placeholder="Select weight" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        <SelectItem value="0.1">0.1 kg</SelectItem>
                        <SelectItem value="0.2">0.2 kg</SelectItem>
                        <SelectItem value="0.5">0.5 kg</SelectItem>
                        {[...Array(65)].map((_, i) => (
                          <SelectItem key={i + 1} value={String(i + 1)}>{i + 1} kg</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="sex">Gender *</Label>
                    <Select value={formData.sex} onValueChange={(value) => handleChange('sex', value)} required>
                      <SelectTrigger data-testid="sex-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="age_years">Age (Years) *</Label>
                    <Input
                      id="age_years"
                      type="number"
                      min="0"
                      value={formData.age_years}
                      onChange={(e) => handleChange('age_years', e.target.value)}
                      required
                      data-testid="age-years-input"
                      className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="microchip_number">Microchip Number</Label>
                  <Input
                    id="microchip_number"
                    value={formData.microchip_number}
                    onChange={(e) => handleChange('microchip_number', e.target.value)}
                    data-testid="microchip-input"
                    className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Additional Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[hsl(var(--info-light))]">
                    <Calendar className="h-5 w-5 text-[hsl(var(--info))]" />
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Additional Details</CardTitle>
                    <CardDescription>Optional information for collection</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="collection_notes">Collection Notes</Label>
                  <Textarea
                    id="collection_notes"
                    value={formData.collection_notes}
                    onChange={(e) => handleChange('collection_notes', e.target.value)}
                    rows={4}
                    data-testid="notes-textarea"
                    className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                {/* Vet Staff Benefit — Collapsible */}
                <div className="rounded-xl bg-primary/5 border border-primary/10 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setStaffBenefitOpen(!staffBenefitOpen)}
                    className="w-full flex items-center justify-between p-4 cursor-pointer hover:bg-primary/10 transition-colors duration-200"
                    data-testid="vet-staff-benefit-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <Heart size={16} className="text-primary" />
                      <span className="font-semibold text-sm">Vet Staff Benefit</span>
                      {formData.vet_staff_benefit !== 'no_benefit' && (
                        <Badge variant="outline" className="ml-2 text-xs border-primary/30 text-primary">
                          {formData.vet_staff_benefit === 'discount_10' ? '10% Discount' : 'Complimentary Urn'}
                        </Badge>
                      )}
                    </div>
                    {staffBenefitOpen ? <ChevronUp size={18} className="text-muted-foreground" /> : <ChevronDown size={18} className="text-muted-foreground" />}
                  </button>

                  {staffBenefitOpen && (
                    <div className="px-4 pb-4 space-y-3">
                      <p className="text-sm text-muted-foreground">
                        Select a benefit for vet clinic staff if applicable
                      </p>
                      <RadioGroup
                        value={formData.vet_staff_benefit}
                        onValueChange={(value) => handleChange('vet_staff_benefit', value)}
                        className="space-y-2"
                        data-testid="vet-staff-benefit-radio"
                      >
                        <Label
                          htmlFor="benefit-none"
                          className={`flex items-center space-x-3 border rounded-lg p-3 cursor-pointer transition-all duration-200 ${
                            formData.vet_staff_benefit === 'no_benefit'
                              ? 'border-primary/30 bg-white shadow-sm'
                              : 'border-border hover:border-primary/20'
                          }`}
                        >
                          <RadioGroupItem value="no_benefit" id="benefit-none" />
                          <span className="flex-1 text-sm">None</span>
                        </Label>
                        <Label
                          htmlFor="benefit-discount"
                          className={`flex items-center space-x-3 border rounded-lg p-3 cursor-pointer transition-all duration-200 ${
                            formData.vet_staff_benefit === 'discount_10'
                              ? 'border-primary/30 bg-white shadow-sm'
                              : 'border-border hover:border-primary/20'
                          }`}
                        >
                          <RadioGroupItem value="discount_10" id="benefit-discount" />
                          <span className="flex-1 text-sm">10% Staff Discount</span>
                        </Label>
                        <Label
                          htmlFor="benefit-urn"
                          className={`flex items-center space-x-3 border rounded-lg p-3 cursor-pointer transition-all duration-200 ${
                            formData.vet_staff_benefit === 'complimentary_urn'
                              ? 'border-primary/30 bg-white shadow-sm'
                              : 'border-border hover:border-primary/20'
                          }`}
                        >
                          <RadioGroupItem value="complimentary_urn" id="benefit-urn" />
                          <span className="flex-1 text-sm">Complimentary Urn</span>
                        </Label>
                      </RadioGroup>
                    </div>
                  )}
                </div>

                {/* NQ Care Payment Section */}
                {isNQCare && (
                  <div className={`p-4 rounded-xl border-2 space-y-3 ${
                    clientPaid === null
                      ? 'border-amber-300 bg-amber-50'
                      : clientPaid
                        ? 'border-green-300 bg-green-50'
                        : 'border-border bg-muted/30'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${clientPaid === null ? 'bg-amber-100' : clientPaid ? 'bg-green-100' : 'bg-muted'}`}>
                        <DollarSign className={`h-5 w-5 ${clientPaid === null ? 'text-amber-600' : clientPaid ? 'text-green-600' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-bold">Did the client pay for this service at NQ Care?</p>
                        {clientPaid === null && (
                          <p className="text-xs text-amber-700 font-medium">Please select an option before submitting</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setClientPaid(true)}
                        className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                          clientPaid === true
                            ? 'border-green-500 bg-green-100 ring-1 ring-green-400'
                            : 'border-border hover:border-green-300 bg-white'
                        }`}
                        data-testid="client-paid-yes"
                      >
                        <p className={`text-sm font-bold ${clientPaid === true ? 'text-green-700' : 'text-foreground'}`}>Yes — Paid at NQ Care</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Client paid through the clinic</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setClientPaid(false)}
                        className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                          clientPaid === false
                            ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                            : 'border-border hover:border-primary/30 bg-white'
                        }`}
                        data-testid="client-paid-no"
                      >
                        <p className={`text-sm font-bold ${clientPaid === false ? 'text-primary' : 'text-foreground'}`}>No — Client has not paid</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Payment not yet collected</p>
                      </button>
                    </div>
                    {clientPaid === true && (
                      <div className="space-y-2 bg-white rounded-xl p-3 border border-green-200">
                        <Label htmlFor="amount_paid" className="text-green-700 font-semibold">Amount Paid ($)</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-2.5 text-green-600 text-sm font-semibold">$</span>
                          <Input
                            id="amount_paid"
                            type="number"
                            min="0"
                            step="0.01"
                            value={amountPaid}
                            onChange={(e) => setAmountPaid(e.target.value)}
                            className="pl-7 transition-all duration-200 focus:ring-2 focus:ring-green-300 border-green-200"
                            data-testid="amount-paid-input"
                          />
                        </div>
                        <p className="text-xs text-green-600">
                          Auto-calculated from pet type & weight. Edit if different.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Emergency Toggle — hidden for mobile vet clinics */}
                {!isMobileVet && (
                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors duration-200">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-[hsl(var(--warning))]" />
                    <div>
                      <Label htmlFor="urgent" className="cursor-pointer font-semibold">Emergency Collection</Label>
                      <p className="text-sm text-muted-foreground">Mark this case as high priority when collection is required due to urgent circumstances at the clinic</p>
                    </div>
                  </div>
                  <Switch
                    id="urgent"
                    checked={formData.urgent}
                    onCheckedChange={(checked) => handleChange('urgent', checked)}
                    data-testid="urgent-toggle"
                  />
                </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Pre-booked Match Banner */}
          {matchedPreBooked && !dismissedMatch && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.3 }}
              className="rounded-2xl border-2 border-amber-300 bg-amber-50 shadow-md overflow-hidden"
              data-testid="prebooked-match-banner"
            >
              <div className="px-5 pt-4 pb-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-amber-800 text-sm">Pre-booked pet found — avoid a double entry</p>
                    <p className="text-amber-700 text-xs mt-0.5">
                      <span className="font-bold">{matchedPreBooked.pet_name}</span>
                      {matchedPreBooked.breed && <span> · {matchedPreBooked.breed}</span>}
                      {matchedPreBooked.weight_kg && <span> · {matchedPreBooked.weight_kg} kg</span>}
                      {matchedPreBooked.client_name && <span> · Owner: {matchedPreBooked.client_name}</span>}
                    </p>
                    <p className="text-amber-600 text-xs mt-1">
                      This pet already has a pre-paid booking. Activate it instead of creating a new submission.
                    </p>
                    {isNQCare && (
                      <div className="mt-2 p-2.5 rounded-lg bg-red-50 border border-red-200">
                        <p className="text-red-700 text-xs font-semibold">
                          This client has already paid Pure Souls directly. If you have charged the client, please process a refund.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {!showActivateForm ? (
                  <div className="flex gap-2 mt-3">
                    <Button
                      type="button"
                      onClick={() => setShowActivateForm(true)}
                      className="flex-1 bg-amber-500 hover:bg-amber-600 text-white border-0 gap-2 h-9"
                      data-testid="activate-prebooked-btn"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Activate Pre-booked Pet
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setDismissedMatch(true); setMatchedPreBooked(null); }}
                      className="px-4 h-9 text-amber-700 border-amber-300 hover:bg-amber-100"
                      data-testid="dismiss-match-btn"
                    >
                      Continue new submission
                    </Button>
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="overflow-hidden mt-3 pt-3 border-t border-amber-200 space-y-3"
                  >
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Confirm a few details</p>

                    {/* Microchip — auto-copied from form */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-amber-800">Microchip Number</label>
                      <input
                        type="text"
                        value={activateMicrochip}
                        onChange={e => setActivateMicrochip(e.target.value)}
                        placeholder="Enter microchip number (optional)"
                        className="w-full text-sm border border-amber-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                        data-testid="activate-microchip-input"
                      />
                      {formData.microchip_number && activateMicrochip === formData.microchip_number && (
                        <p className="text-[11px] text-amber-600">Auto-filled from your form entry</p>
                      )}
                    </div>

                    {/* Emotional State — auto-copied from form */}
                    <div className="space-y-1.5">
                      <p className="text-xs text-amber-700 font-medium">
                        Owner Emotional State
                        {formData.owner_emotional_state && activateEmotional === formData.owner_emotional_state && (
                          <span className="ml-2 text-amber-600 font-normal">· copied from your selection above</span>
                        )}
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                        {[
                          { value: 'very_distressed', label: 'Very Distressed', dot: 'bg-red-500',    ring: 'border-red-300 bg-red-50',    inactive: 'border-amber-200 hover:border-red-200' },
                          { value: 'emotional',       label: 'Emotional',       dot: 'bg-orange-400', ring: 'border-orange-300 bg-orange-50', inactive: 'border-amber-200 hover:border-orange-200' },
                          { value: 'calm',            label: 'Calm',            dot: 'bg-green-500',  ring: 'border-green-300 bg-green-50',  inactive: 'border-amber-200 hover:border-green-200' },
                          { value: 'not_present',     label: 'Not Present',     dot: 'bg-gray-400',   ring: 'border-gray-300 bg-gray-50',    inactive: 'border-amber-200' },
                          { value: 'unsure',          label: 'Unsure',          dot: 'bg-slate-400',  ring: 'border-slate-300 bg-slate-50',  inactive: 'border-amber-200' },
                        ].map(opt => (
                          <button key={opt.value} type="button"
                            onClick={() => setActivateEmotional(activateEmotional === opt.value ? '' : opt.value)}
                            className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-all text-xs cursor-pointer ${activateEmotional === opt.value ? opt.ring + ' border-2' : opt.inactive + ' border'}`}
                          >
                            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${opt.dot}`} />
                            <span className="font-medium truncate">{opt.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Collection Notes — auto-copied from form */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-amber-800">
                        Collection Notes
                        {formData.collection_notes && activateNotes === formData.collection_notes && (
                          <span className="ml-2 text-amber-600 font-normal">· copied from your notes above</span>
                        )}
                      </label>
                      <textarea
                        value={activateNotes}
                        onChange={e => setActivateNotes(e.target.value)}
                        placeholder="Add any collection notes (optional)"
                        rows={3}
                        className="w-full text-sm border border-amber-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                        data-testid="activate-notes-input"
                      />
                      {activateNotes && (
                        <p className="text-[11px] text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" /> These notes will be sent with the activation
                        </p>
                      )}
                    </div>

                    <div className="flex gap-2 pt-1">
                      <Button
                        type="button"
                        onClick={handleActivatePreBooked}
                        disabled={activating}
                        className="flex-1 bg-amber-500 hover:bg-amber-600 text-white border-0 gap-2"
                        data-testid="activate-confirm-btn"
                      >
                        {activating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        {activating ? 'Activating...' : 'Confirm Activation'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowActivateForm(false)}
                        className="px-4 border-amber-300 text-amber-700 hover:bg-amber-100">
                        Back
                      </Button>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}

          {/* Submit Button - Sticky on Mobile */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="sticky bottom-4 md:static z-20"
          >
            <Card className="border-0 shadow-xl md:shadow-md">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 h-12 text-base font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                    data-testid="submit-request-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Submitting Request...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="mr-2 h-5 w-5" />
                        Submit Collection Request
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    disabled={loading}
                    className="h-12 w-full sm:w-auto"
                    data-testid="cancel-request-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </form>
      </div>
    </Layout>
  );
};

export default NewRequest;
